/**
 * structure_pivot — the seating + default-size law for STRUCTURE-class catalog
 * models (`structure:*`) placed by Edit Mode (freeform `part` rows and
 * `admin_placement` rows). Pure — no three import, so harnesses can assert it.
 *
 * SEATING. The cooked settlement GLBs are OFF-PIVOT: bbox centers sit 40–1290 m
 * from the model origin and bases float 70–130 m up (the cooker normalized raw
 * arrays without node transforms — see CommonsSettlementsModule, collider wave
 * 2026-07-22). The settlement renderer seats every instance by its measured
 * bbox BOTTOM-CENTER; Edit Mode placements must do the same or a building
 * placed at the marker renders in the sky, tens of metres away (2026-08-20).
 *
 * The row keeps the FOOTPRINT point the player clicked (so the gizmo pivots on
 * the building and scaling grows it in place); the renderers translate the
 * model by the natural (scale-1) offset inside a wrapper group, which the row's
 * rotation/scale then transform for free. A mesh-space collider proxy needs its
 * body moved by the same offset, yaw-rotated and scaled (`pivotBodyPos`).
 * GATED to `structure:` keys — props / decor / uploads keep their authored
 * pivots, so nothing placed before this law moves.
 *
 * SIZE. These GLBs normalize to ~1.9 m, so a raw placement is a dollhouse.
 * `fitScaleFor` fits a measured model to a real height (civ 14 m; legacy
 * 10/20 m by class), capped by footprint so a wide flat complex never swallows
 * the street; deed types reuse their deployed-structure scale; the far-flung-
 * vertex landmarks take the settlement renderer's hand scales.
 */
export interface Vec3Like { x: number; y: number; z: number }
export interface Box3Like { min: Vec3Like; max: Vec3Like }

export const isStructureKey = (key: string | null | undefined): boolean =>
  typeof key === 'string' && key.startsWith('structure:');

/** Natural-scale translation that puts the bbox bottom-center at the origin. */
export function footprintOffset(bb: Box3Like): Vec3Like {
  return { x: -(bb.min.x + bb.max.x) / 2, y: -bb.min.y, z: -(bb.min.z + bb.max.z) / 2 };
}

/**
 * The seating decision for one model: civ keys are ALWAYS seated (known
 * off-pivot); any other structure is seated only when its origin lies OUTSIDE
 * its own bounds expanded by its size — a well-pivoted legacy/deed model returns
 * null and renders exactly as before, so nothing placed before this law moves.
 */
export function seatingOffset(key: string | null | undefined, bb: Box3Like): Vec3Like | null {
  if (!isStructureKey(key)) return null;
  const off = footprintOffset(bb);
  if (/^structure:civ[ab]_/.test(key as string)) return off;
  const sx = bb.max.x - bb.min.x, sy = bb.max.y - bb.min.y, sz = bb.max.z - bb.min.z;
  const originInside = bb.min.x - sx <= 0 && 0 <= bb.max.x + sx
    && bb.min.y - sy <= 0 && 0 <= bb.max.y + sy
    && bb.min.z - sz <= 0 && 0 <= bb.max.z + sz;
  return originInside ? null : off;
}

/** Body translation for a mesh-space collider under the same offset (Y-yaw + uniform scale). */
export function pivotBodyPos(pos: Vec3Like, yaw: number, scale: number, off: Vec3Like): Vec3Like {
  const ox = off.x * scale, oy = off.y * scale, oz = off.z * scale;
  const c = Math.cos(yaw), s = Math.sin(yaw);
  return { x: pos.x + (ox * c + oz * s), y: pos.y + oy, z: pos.z + (-ox * s + oz * c) };
}

export const CITY_BUILDING_H_M = 14;
export const LEGACY_TALL_H_M = 20;
export const LEGACY_LOW_H_M = 10;
const TALL_RE = /tower|spire|terminal|hub|hall|monument|station|academy|chamber|observatory|spaceport|reclamation|dome|arena|factory/;
/** Hand scales matching CommonsSettlementsModule's fixedScale landmarks — their raw bboxes are polluted by far-flung cooked verts. */
const FIXED_SCALE: Record<string, number> = { 'structure:civa_ship': 1.35, 'structure:landing_pad_structure': 9 };

/** Default placement scale so a structure arrives at a REAL size (the Scale tool refines). */
export function fitScaleFor(key: string, size: Vec3Like, deedScale?: number): number {
  const fixed = FIXED_SCALE[key];
  if (fixed !== undefined) return fixed;
  if (deedScale) return deedScale; // deed types match their deployed-structure renderer
  const type = key.slice('structure:'.length);
  const targetH = /^civ[ab]_/.test(type) ? CITY_BUILDING_H_M : TALL_RE.test(type) ? LEGACY_TALL_H_M : LEGACY_LOW_H_M;
  const h = Math.max(0.05, size.y);
  const foot = Math.max(0.05, size.x, size.z);
  const s = Math.min(targetH / h, (2.2 * targetH) / foot);
  return Math.max(0.25, Math.min(40, s));
}
